import { useRef } from "react";
import { useAuth } from "../../../contexts/auth/Context";
import { decodeUserInfo } from "../../../utils/user";
import { useUserInfo } from "../../../hooks/user/useUserInfo";
import { useUploadUserAvatar } from "../../../hooks/user/avatar/useUploadUserAvatar";
import { useToast } from "../../../contexts/toast/Context";
import { formatDateWithoutTimeAndInWords } from "../../../utils/format";
import { InputField } from "../../common/ui/inputField";
import { Button } from "../../common/ui/button/button";
import { Avatar } from "../../common/ui/avatar";
import { getResourceUrl } from "../../../utils/images";

function getInitials(
  firstName: string | null,
  lastName: string | null,
  email: string | null,
): string {
  if (firstName?.trim() && lastName?.trim()) {
    return `${firstName.trim()[0]}${lastName.trim()[0]}`.toUpperCase();
  }
  if (firstName?.trim()) return firstName.trim().slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return "?";
}

const ReadonlyField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="space-y-1">
    <label className="text-label">{label}</label>
    <div className="card-section">
      <p className="text-size-sm font-medium text-global-primary">{value}</p>
    </div>
  </div>
);

export const UserInfo = () => {
  const { token } = useAuth();
  const userInfo = decodeUserInfo(token);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    userProfile,
    isLoading,
    isLoadingProfile,
    isEditing,
    firstName,
    lastName,
    setFirstName,
    setLastName,
    handleSubmit,
    handleCancel,
    handleEdit,
  } = useUserInfo();
  const { uploadAvatar, isCompressing, isUploading } = useUploadUserAvatar();
  const { showToast, removeToast } = useToast();

  const hasProfilePicture = Boolean(userProfile?.profilePictureKey);
  const avatarUrl = hasProfilePicture
    ? getResourceUrl(userProfile!.profilePictureKey!)
    : "";

  const displayName =
    [firstName?.trim(), lastName?.trim()].filter(Boolean).join(" ") || null;

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const loadingId = showToast("Subiendo imagen de avatar...", "loading");
    try {
      await uploadAvatar(file);
      removeToast(loadingId);
      showToast("Imagen de avatar cargada correctamente", "success");
    } catch {
      removeToast(loadingId);
      showToast("Error al cargar la imagen de avatar", "error");
    }
  };

  return (
    <div className="rounded-xl bg-global-primary border border-global-primary shadow-global-card overflow-hidden">
      {/* Gradient banner */}
      <div className="relative h-28 btn-global-primary-gradient overflow-hidden">
        <div className="absolute inset-0 gradient-accent-overlay opacity-40 pointer-events-none" />
      </div>

      {/* Avatar + identity */}
      <div className="-mt-14 flex flex-col items-center px-4 pb-5">
        {/* Avatar with upload overlay */}
        <div className="relative mb-3">
          <div
            className="w-28 h-28 rounded-full overflow-hidden bg-global-overlay-secondary"
            style={{
              boxShadow:
                "0 0 0 4px var(--bg-primary), 0 0 0 6px var(--border-primary)",
            }}
          >
            <Avatar
              src={avatarUrl}
              alt=""
              fallback={getInitials(
                firstName,
                lastName,
                userInfo.email ?? null,
              )}
              className="w-full h-full"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleAvatarChange}
            disabled={isCompressing || isUploading}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCompressing || isUploading || isLoadingProfile}
            className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity text-white text-size-2xs font-medium text-center px-2 cursor-pointer"
            title="Cambiar imagen de avatar"
          >
            {isCompressing
              ? "Comprimiendo..."
              : isUploading
                ? "Subiendo..."
                : "Cambiar foto"}
          </button>
        </div>

        {/* Name and email */}
        <h2 className="text-title-section text-center">
          {displayName ?? (
            <span className="text-global-tertiary font-normal italic">
              Sin nombre
            </span>
          )}
        </h2>
        <p className="text-size-sm text-global-muted mt-0.5">
          {userInfo.email}
        </p>

        {/* Role badge */}
        {userInfo.role && (
          <span className="mt-2 inline-flex items-center px-3 py-0.5 rounded-full text-size-2xs font-semibold bg-global-overlay-secondary text-global-secondary border border-global-secondary tracking-wide uppercase">
            {userInfo.role}
          </span>
        )}

        {/* Edit button */}
        {!isEditing && (
          <Button
            onClick={handleEdit}
            disabled={isLoadingProfile}
            size="sm"
            className="mt-4"
          >
            <span className="flex items-center gap-2">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Editar perfil
            </span>
          </Button>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-global-primary" />

      {/* Fields */}
      <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
        {isEditing ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                id="firstName"
                type="text"
                label="Nombre"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                placeholder="Ingresa tu nombre"
              />
              <InputField
                id="lastName"
                type="text"
                label="Apellido"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
                placeholder="Ingresa tu apellido"
              />
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-global-primary">
              <Button
                type="submit"
                isLoading={isLoading}
                loadingText="Guardando..."
                fullWidth
                className="flex-1"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Guardar cambios
                </span>
              </Button>
              <Button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                size="sm"
                variant="secondary"
                text="Cancelar"
                className="btn-modal-cancel"
              />
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ReadonlyField
              label="Email"
              value={
                userInfo.email ?? (
                  <span className="text-global-tertiary italic">
                    No disponible
                  </span>
                )
              }
            />
            {userProfile?.createdAt && (
              <ReadonlyField
                label="Miembro desde"
                value={formatDateWithoutTimeAndInWords(userProfile.createdAt)}
              />
            )}
            <ReadonlyField
              label="Nombre"
              value={
                firstName?.trim() || (
                  <span className="text-global-tertiary italic">
                    No especificado
                  </span>
                )
              }
            />
            <ReadonlyField
              label="Apellido"
              value={
                lastName?.trim() || (
                  <span className="text-global-tertiary italic">
                    No especificado
                  </span>
                )
              }
            />
          </div>
        )}
      </form>
    </div>
  );
};
